import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import useDebounce from "../../../hooks/useDebounce";
import messages from "../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  deleteCompanyRoleRequest,
  getCompanyRolesRequest,
  updateCompanyRoleRequest,
} from "../../../store/CompanyPortal/Roles/actions";
import {
  UIButton,
  UIPaginationBar,
} from "../../../components/ui";
import TeamToolbar from "./TeamToolbar";
import RolesTable from "./RolesTable";
import DeleteRoleModal from "./DeleteRoleModal";
import TeamListEmptyState from "./TeamListEmptyState";
import { ROLE_STATUS, buildCompanyRolesListParams } from "./roleMappers";
import { companyPermissionModules } from "../../../utils/Constants";
import {
  canAddCompanyModule,
  canDeleteCompanyModule,
  canEditCompanyModule,
} from "../../../utils/Permissions";
import { UI_TABLE_DEFAULT_PAGE_SIZE } from "../../../utils/tablePageSize";

const ROLES_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;

const RolesManagement = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const storeRows = useSelector((state) => state.companyRoles.list);
  const meta = useSelector((state) => state.companyRoles.meta);
  const deleting = useSelector((state) => state.companyRoles.deleting);
  const login = useSelector((state) => state.auth.login);
  const copy = messages[lang].teamManagement;
  const rolesCopy = copy.roles;
  const rolesModule = companyPermissionModules.rolesManagement;
  const canAdd = canAddCompanyModule(rolesModule, login);
  const canEdit = canEditCompanyModule(rolesModule, login);
  const canDelete = canDeleteCompanyModule(rolesModule, login);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(UI_TABLE_DEFAULT_PAGE_SIZE);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const listParams = useMemo(
    () =>
      buildCompanyRolesListParams({
        search: debouncedSearch,
        page,
        pageSize,
      }),
    [debouncedSearch, page, pageSize],
  );

  useEffect(() => {
    dispatch(getCompanyRolesRequest(listParams));
  }, [dispatch, lang, listParams]);

  useEffect(() => {
    setRows(storeRows);
  }, [storeRows]);

  const total = meta.count || 0;
  const pages = meta.pages || Math.max(Math.ceil(total / pageSize), 1);
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  // Contract §4.3: optimistic toggle, roll back on non-2xx.
  const handleToggleStatus = (role, checked) => {
    if (!canEdit) return;
    const previousActive = role.active;
    const nextStatus = checked ? ROLE_STATUS.enabled : ROLE_STATUS.disabled;
    const prevStatus = previousActive
      ? ROLE_STATUS.enabled
      : ROLE_STATUS.disabled;

    setRows((current) =>
      current.map((item) =>
        item.id === role.id
          ? { ...item, active: checked, status: nextStatus }
          : item,
      ),
    );

    dispatch(
      updateCompanyRoleRequest({
        id: role.id,
        data: { status: nextStatus },
        successMessage: messages[lang].successMsgs.updatedSuccessfully,
        onError: () => {
          setRows((current) =>
            current.map((item) =>
              item.id === role.id
                ? { ...item, active: previousActive, status: prevStatus }
                : item,
            ),
          );
        },
      }),
    );
  };

  const handleCreateRole = () => {
    history.push(ROUTE_PATHS.companyTeamRoleCreate);
  };

  const handleViewRole = (role) => {
    if (!role?.id) return;
    history.push(
      ROUTE_PATHS.companyTeamRoleView.replace(":id", role.id),
    );
  };

  const handleEditRole = (role) => {
    if (!role?.id) return;
    history.push({
      pathname: ROUTE_PATHS.companyTeamRoleView.replace(":id", role.id),
      state: { startInEdit: true },
    });
  };

  const handleDeleteRole = (role) => {
    setRoleToDelete(role);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setRoleToDelete(null);
  };

  // Contract §3.5 / §4.4 — DELETE then refetch on success. Trash icon is
  // hidden when usersCount > 0 or systemRole, so the guard here is defensive.
  const confirmDeleteRole = () => {
    if (!roleToDelete || roleToDelete.usersCount > 0 || deleting) return;
    dispatch(
      deleteCompanyRoleRequest({
        id: roleToDelete.id,
        onSuccess: () => {
          setRoleToDelete(null);
          dispatch(getCompanyRolesRequest(listParams));
        },
      }),
    );
  };

  return (
    <Box className="team-management__panel">
      <TeamToolbar
        search={search}
        searchPlaceholder={rolesCopy.searchPlaceholder}
        onSearchChange={handleSearchChange}
      >
        {canAdd && (
          <UIButton
            variant="outlined"
            size="large"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateRole}
            className="team-management__add"
          >
            {rolesCopy.createRole}
          </UIButton>
        )}
      </TeamToolbar>

      {rows.length ? (
        <>
          <RolesTable
            rows={rows}
            pageSize={pageSize}
            labels={rolesCopy.table}
            onView={handleViewRole}
            onToggleStatus={handleToggleStatus}
            onEdit={handleEditRole}
            onDelete={handleDeleteRole}
            canEdit={canEdit}
            canDelete={canDelete}
          />
          <UIPaginationBar
            currentPage={page}
            pages={pages}
            pageSize={pageSize}
            pageSizeOptions={ROLES_PAGE_SIZE_OPTIONS}
            rangeTemplate={rolesCopy.rangeTemplate}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            rangeTotal={total}
            rowsPerPageLabel={copy.rowsPerPageLabel}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      ) : (
        <TeamListEmptyState
          searchTerm={debouncedSearch}
          total={total}
          copy={rolesCopy}
          canAdd={canAdd}
          actionLabel={rolesCopy.createRole}
          onAction={handleCreateRole}
        />
      )}

      <DeleteRoleModal
        open={Boolean(roleToDelete)}
        role={roleToDelete}
        copy={rolesCopy.deleteModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteRole}
      />
    </Box>
  );
};

export default RolesManagement;
